import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../api';
import { API_ENDPOINTS, STORAGE_KEYS, MOCK_TEAM_MEMBERS } from '../config';
import { useI18n } from '../i18n';
import './Leaderboard.css';

// 模拟数据
const MOCK_DATA = [
  {
    team: "智行者团队",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 85.67,
          score_route: 92.34,
          score_penalty: 0.93
        }
      }
    }
  },
  {
    team: "自动驾驶先锋",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 78.45,
          score_route: 88.12,
          score_penalty: 0.89
        }
      }
    }
  },
  {
    team: "未来出行",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 72.33,
          score_route: 85.67,
          score_penalty: 0.84
        }
      }
    }
  },
  {
    team: "极速智驾",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 68.90,
          score_route: 82.45,
          score_penalty: 0.84
        }
      }
    }
  },
  {
    team: "智慧交通实验室",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 65.21,
          score_route: 78.90,
          score_penalty: 0.83
        }
      }
    }
  },
  {
    team: "无人驾驶创新组",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 58.76,
          score_route: 75.32,
          score_penalty: 0.78
        }
      }
    }
  },
  {
    team: "AI驾驶联盟",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 52.44,
          score_route: 70.88,
          score_penalty: 0.74
        }
      }
    }
  },
  {
    team: "智能车队",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 45.12,
          score_route: 65.43,
          score_penalty: 0.69
        }
      }
    }
  },
  {
    team: "自动驾驶研究院",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 38.99,
          score_route: 58.76,
          score_penalty: 0.66
        }
      }
    }
  },
  {
    team: "新手上路队",
    _checkpoint: {
      global_record: {
        scores_mean: {
          score_composed: 12.34,
          score_route: 25.67,
          score_penalty: 0.48
        }
      }
    }
  }
];

const Leaderboard = ({ showNav = true, competitionId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const { t, basePath } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false); // 新增

  // 检查用户是否已登录
  const isLoggedIn = localStorage.getItem(STORAGE_KEYS.isLoggedIn) === 'true';
  const username = localStorage.getItem(STORAGE_KEYS.username);
  const registryUrl = localStorage.getItem(STORAGE_KEYS.registryUrl) || '—';
  const role = localStorage.getItem(STORAGE_KEYS.role) || 'user';
  const roleLabel = role === 'siteAdmin'
    ? t('role.siteAdmin')
    : role === 'competitionAdmin'
      ? t('role.competitionAdmin')
      : role === 'user'
        ? t('role.user')
        : role;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 根据文档，URL格式为 /api/leaderboard/{competition-id}
        // 如果没有 competitionId，这里可能需要一个默认值或者不同的API，此处假设必须有ID或使用旧逻辑兼容
        
        if (!competitionId) {
          throw new Error(t('leaderboard.missingCompetition'));
        }

        const jsonData = await apiRequest(API_ENDPOINTS.leaderboard(competitionId));
        
        const sortedData = jsonData
          .map((item) => ({
            team: item.team,
            scoreComposed: item._checkpoint?.global_record?.scores_mean?.score_composed || 0,
            scoreRoute: item._checkpoint?.global_record?.scores_mean?.score_route || 0,
            scorePenalty: item._checkpoint?.global_record?.scores_mean?.score_penalty || 0,
          }))
          .sort((a, b) => b.scoreComposed - a.scoreComposed)
          .map((item, index) => ({
            ...item,
            rank: index + 1,
          }));
        
        setData(sortedData);
        setLoading(false);
      } catch (err) {
        // 请求失败时使用模拟数据
        console.warn('使用模拟数据:', err.message);
        const sortedData = MOCK_DATA
          .map((item) => ({
            team: item.team,
            scoreComposed: item._checkpoint.global_record.scores_mean.score_composed,
            scoreRoute: item._checkpoint.global_record.scores_mean.score_route,
            scorePenalty: item._checkpoint.global_record.scores_mean.score_penalty,
          }))
          .sort((a, b) => b.scoreComposed - a.scoreComposed)
          .map((item, index) => ({
            ...item,
            rank: index + 1,
          }));

        setData(sortedData);
        setUsingMockData(true);
        setLoading(false);
      }
    };

    fetchData();
  }, [competitionId]);

  const getRankClass = (rank) => {
    if (rank === 1) return 'rank-gold';
    if (rank === 2) return 'rank-silver';
    if (rank === 3) return 'rank-bronze';
    return '';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return rank;
  };

  const navigate = useNavigate();

  const handleToggle = (e, team) => {
      e.stopPropagation(); // 防止触发航跳转
      setExpandedTeam((prev) => (prev === team ? null : team));
  };

  if (loading) {
    return (
      <div className="leaderboard-loading">
        <div className="loading-spinner"></div>
        <span>{t('leaderboard.loading')}</span>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      {/* 导航栏 */}
      {showNav && (
        <nav className="navbar">
          <div className="nav-brand">
            <span className="nav-logo">🚗</span>
            <span className="nav-title">{t('app.name')}</span>
          </div>

          <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? '✕' : '☰'}
          </button>

          <div className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
            <Link to={`${basePath}/leaderboard`} className="nav-link">{t('nav.home')}</Link>
            <Link to={`${basePath}/leaderboard/competitions`} className="nav-link">{t('nav.competitions')}</Link>
            {isLoggedIn ? (
              <>
                <Link to={`${basePath}/leaderboard/dashboard`} className="nav-link">{t('nav.dashboard')}</Link>
                <div className="user-menu">
                  <div className="user-menu-trigger">👤 {username}</div>
                  <div className="user-menu-panel">
                    <div className="user-menu-row">
                      <span className="user-menu-label">{t('user.username')}</span>
                      <span>{username}</span>
                    </div>
                    <div className="user-menu-row">
                      <span className="user-menu-label">{t('user.role')}</span>
                      <span>{roleLabel}</span>
                    </div>
                    <div className="user-menu-row">
                      <span className="user-menu-label">{t('user.registry')}</span>
                      <span>{registryUrl}</span>
                    </div>
                    <button className="user-menu-logout" onClick={() => {
                      localStorage.removeItem(STORAGE_KEYS.isLoggedIn);
                      localStorage.removeItem(STORAGE_KEYS.username);
                      localStorage.removeItem(STORAGE_KEYS.accessToken);
                      localStorage.removeItem(STORAGE_KEYS.registryUrl);
                      localStorage.removeItem(STORAGE_KEYS.role);
                      navigate(`${basePath}/leaderboard`);
                    }}>{t('user.logout')}</button>
                  </div>
                </div>
              </>
            ) : (
              <Link to={`${basePath}/leaderboard/login`} className="nav-link nav-login">{t('nav.login')}</Link>
            )}
          </div>
        </nav>
      )}

      <div className="leaderboard-header">
        <h1 className="leaderboard-title">🏆 {t('leaderboard.title')}</h1>
        <p className="leaderboard-subtitle">{t('leaderboard.subtitle')}</p>
      </div>
      
      {usingMockData && (
        <div className="mock-data-notice">
          <span className="notice-icon">⚠️</span>
          {t('leaderboard.mockNotice')}
        </div>
      )}
      
      <div className="leaderboard-table-wrapper">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th>{t('leaderboard.rank')}</th>
              <th>{t('leaderboard.team')}</th>
              <th>{t('leaderboard.score.total')}</th>
              <th>{t('leaderboard.score.route')}</th>
              <th>{t('leaderboard.score.penalty')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr
                key={item.team}
                className={`${getRankClass(item.rank)} leaderboard-row ${expandedTeam === item.team ? 'expanded' : ''}`}
                onClick={(e) => handleToggle(e, item.team)}
              >
                <td className="rank-cell">
                  <span className="rank-badge">{getRankIcon(item.rank)}</span>
                </td>
                <td className="team-cell">
                  <span className="team-name">{item.team}</span>
                </td>
                <td className="score-composed">
                  <span className="score-value">{item.scoreComposed?.toFixed(2)}</span>
                </td>
                <td className="score-route">
                  <span className="score-value">{item.scoreRoute?.toFixed(2)}</span>
                </td>
                <td className="score-penalty">
                  <span className="score-value">{item.scorePenalty?.toFixed(2)}</span>
                  <div className={`row-members ${expandedTeam === item.team ? 'show' : ''}`}>
                    <span className="members-title">{t('leaderboard.members')}：</span>
                    {(item.members || MOCK_TEAM_MEMBERS[item.team] || []).slice(0, 6).map((member) => (
                      <span key={member} className="member-name">{member}</span>
                    ))}
                    {(item.members || MOCK_TEAM_MEMBERS[item.team] || []).length > 6 && (
                      <span className="member-name">…</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Leaderboard;


